import React, { useMemo } from 'react';
import {
  FileText,
  AlertTriangle,
  Shield,
  AlertCircle,
  TrendingUp,
  Key,
  UserCheck,
  Database
} from 'lucide-react';
import { useIsMobile } from '../../../hooks/use-mobile';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { Badge } from '../../../components/ui/badge';

// Static IAM tags data
const STATIC_IAM_TAGS = {
  root_account_access_keys_present: 12,
  privilege_escalation: 8,
  over_permissive_access: 45,
  data_exposure: 23,
  credential_risk: 34,
  iam_policies_with_full_administrative_privileges: 19,
  identity_misuse: 15,
  misconfiguration: 28,
  compliance_gap: 16,
  general_security_risk: 22,
  iam_role_policy_too_permissive: 14,
  enable_mfa_for_iam_users_with_console_password: 9
};

// Available color palette for dynamic assignment
const colorPalette = [
  { color: 'bg-red-500/10 border-red-500/20', textColor: 'text-red-400' },
  { color: 'bg-orange-500/10 border-orange-500/20', textColor: 'text-orange-400' },
  { color: 'bg-yellow-500/10 border-yellow-500/20', textColor: 'text-yellow-400' },
  { color: 'bg-green-500/10 border-green-500/20', textColor: 'text-green-400' },
  { color: 'bg-blue-500/10 border-blue-500/20', textColor: 'text-blue-400' },
  { color: 'bg-purple-500/10 border-purple-500/20', textColor: 'text-purple-400' },
  { color: 'bg-pink-500/10 border-pink-500/20', textColor: 'text-pink-400' },
  { color: 'bg-indigo-500/10 border-indigo-500/20', textColor: 'text-indigo-400' },
  { color: 'bg-teal-500/10 border-teal-500/20', textColor: 'text-teal-400' },
  { color: 'bg-cyan-500/10 border-cyan-500/20', textColor: 'text-cyan-400' },
  { color: 'bg-emerald-500/10 border-emerald-500/20', textColor: 'text-emerald-400' },
  { color: 'bg-violet-500/10 border-violet-500/20', textColor: 'text-violet-400' },
  { color: 'bg-rose-500/10 border-rose-500/20', textColor: 'text-rose-400' },
  { color: 'bg-amber-500/10 border-amber-500/20', textColor: 'text-amber-400' },
  { color: 'bg-lime-500/10 border-lime-500/20', textColor: 'text-lime-400' }
];

const iconPalette = [
  AlertTriangle,
  Shield,
  AlertCircle,
  Database,
  Key,
  UserCheck,
  FileText,
  TrendingUp
];

const getColorForIndex = (index) => {
  return colorPalette[index % colorPalette.length];
};

const getIconForIndex = (index) => {
  return iconPalette[index % iconPalette.length];
};

const formatFieldName = (fieldName) => {
  // Map for custom formatting
  const customNames = {
    'compliance_gap': 'Compliance Gap',
    'credential_risk': 'Credential Risk',
    'data_exposure': 'Data Exposure',
    'general_security_risk': 'General Security Risk',
    'iam_policies_with_full_administrative_privileges': 'Full Admin Privileges',
    'identity_misuse': 'Identity Misuse',
    'informational': 'Informational',
    'misconfiguration': 'Misconfiguration',
    'over_permissive_access': 'Over-Permissive Access',
    'privilege_escalation': 'Privilege Escalation',
    'root_account_access_keys_present': 'Root Access Keys',
    'iam_role_policy_too_permissive': 'Too Permissive Role Policy',
    'enable_mfa_for_iam_users_with_console_password': 'MFA Not Enabled'
  };

  if (customNames[fieldName]) {
    return customNames[fieldName];
  }

  return fieldName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const Insights = () => {
  const isMobile = useIsMobile();
  const iamTags = STATIC_IAM_TAGS;

  const insightsData = useMemo(() => {
    const cards = [];
    
    // Add Total IAM Findings card if we have IAM data
    const totalIamFindings = Object.values(iamTags).reduce((sum, count) => sum + count, 0);
    if (totalIamFindings > 0) {
      cards.push({
        title: 'Total IAM Findings',
        value: totalIamFindings,
        icon: AlertTriangle,
        color: 'bg-red-500/10 border-red-500/20',
        textColor: 'text-red-400',
        isTotal: true
      });
    }

    const getSeverity = (key) => {
      const high = [
        'root_account_access_keys_present',
        'privilege_escalation',
        'over_permissive_access',
        'data_exposure',
        'credential_risk',
        'iam_policies_with_full_administrative_privileges',
        'identity_misuse'
      ];
      const medium = [
        'misconfiguration',
        'compliance_gap',
        'general_security_risk',
        'iam_role_policy_too_permissive',
        'enable_mfa_for_iam_users_with_console_password'
      ];
      
      if (high.includes(key)) return 'High';
      if (medium.includes(key)) return 'Medium';
      return 'Low';
    };

    // Convert IAM tags to array and sort by severity then count
    const iamTagsArray = Object.entries(iamTags)
      .map(([key, count]) => ({ key, count, severity: getSeverity(key) }))
      .sort((a, b) => {
        const severityOrder = { High: 3, Medium: 2, Low: 1 };
        const sevA = severityOrder[a.severity] || 0;
        const sevB = severityOrder[b.severity] || 0;
        
        if (sevA !== sevB) {
          return sevB - sevA; // Higher severity first
        }
        return b.count - a.count; // Higher count first
      });

    // Add IAM tag cards
    iamTagsArray.forEach(({ key, count, severity }, index) => {
      const IconComponent = getIconForIndex(index);
      const colors = getColorForIndex(index);

      cards.push({
        title: formatFieldName(key),
        value: count,
        icon: IconComponent,
        color: colors.color,
        textColor: colors.textColor,
        key: key,
        severity: severity
      });
    });

    return cards;
  }, [iamTags]);

  const optimizationPercentage = useMemo(() => {
    if (!iamTags || Object.keys(iamTags).length === 0) return 0;
    const total = Object.values(iamTags).reduce((sum, count) => sum + count, 0);
    if (total === 0) return 0;
    
    // High severity keys matching the table logic
    const highSeverityKeys = [
      'root_account_access_keys_present',
      'privilege_escalation',
      'over_permissive_access',
      'data_exposure',
      'credential_risk',
      'iam_policies_with_full_administrative_privileges',
      'identity_misuse'
    ];

    const criticalIssues = highSeverityKeys.reduce((acc, key) => acc + (iamTags[key] || 0), 0);
    
    return Math.round((criticalIssues / total) * 100);
  }, [iamTags]);

  return (
    <TooltipProvider>
      <Card className={`bg-white border-[#CBC8C89C] flex flex-col w-full h-full ${isMobile ? 'rounded-xl' : 'rounded-2xl'}`}>
        <CardHeader className={isMobile ? 'px-4 py-3' : 'px-6 py-5 pb-0'}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className={`font-semibold text-black font-inter tracking-[-0.025em] ${isMobile ? 'text-sm' : 'text-lg'}`}>
                Cloud IAM Insights
              </CardTitle>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className={`w-5 h-5 rounded-full border border-black/35 bg-white flex items-center justify-center text-black/35 hover:bg-gray-50 transition-colors ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    <span className="font-semibold">i</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Overview of IAM security findings and risk assessment</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className={`px-3 py-1 rounded-full bg-[#FF696942] border-[#DC3131] border text-[#DC3131] tracking-tighter ${isMobile ? 'text-xs' : 'text-xs'}`}>
              <span className="text-orange-600 font-medium">{optimizationPercentage}% critical issues</span>
            </div>
          </div>
          <p className="text-black text-sm mt-1">
            Overview of IAM security policies and risk assessment
          </p>
        </CardHeader>

        <CardContent className={`${isMobile ? 'px-4 pb-4' : 'px-6 pb-6'} w-full flex-1 flex flex-col`}>
          {/* Stats Section - 2 Column Clean List */}
          {insightsData.length > 0 && (
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-3 ${isMobile ? 'mt-4' : 'mt-6'}`}>
              {insightsData.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div 
                    key={item.key || index} 
                    className="group flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-white hover:border-gray-300 hover:shadow-sm transition-all duration-200"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`p-1.5 rounded-md ${item.color} flex-shrink-0 flex items-center justify-center`}>
                        <IconComponent className={`w-4 h-4 ${item.textColor}`} />
                      </div>
                      <span className="text-sm font-medium text-gray-700 truncate pr-2" title={item.title}>
                        {item.title}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                      {!item.isTotal && (
                        <Badge 
                          variant="outline"
                          className={
                            `font-medium px-2 py-0.5 text-[10px] border rounded-full shadow-none ${
                              item.severity === 'High' ? 'bg-red-50 text-red-700 border-red-200' :
                              item.severity === 'Medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                              'bg-blue-50 text-blue-700 border-blue-200'
                            }`
                          }
                        >
                          {item.severity}
                        </Badge>
                      )}
                      {item.isTotal && (
                        <Badge variant="outline" className="font-medium px-2 py-0.5 text-[10px] bg-gray-50 text-gray-700 border-gray-200 rounded-full">
                          Total
                        </Badge>
                      )}
                      <span className="text-sm font-bold text-gray-900 min-w-[1.5rem] text-right">
                        {item.value}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty State */}
          {insightsData.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center bg-gray-50 rounded-xl mt-6 border border-dashed border-gray-200">
              <div className="bg-white p-3 rounded-full shadow-sm mb-3">
                <Shield className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-gray-900 font-medium mb-1">No Insights Found</h3>
              <p className="text-gray-500 text-sm max-w-[250px]">
                No IAM security findings were detected in your scanned environments.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

export default Insights;
